import React, { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';

// Shared maintenance integration. Changes are distributed from the backend's maintenance/client directory.
export default function MaintenanceGate({ children, siteCode, statusUrl, loginPath = '/login', passUrl, apiClient, pathname = window.location.pathname }) {
  const generation = useRef(0);
  const busy = useRef(false);
  const storageKey = `ss-maintenance:${siteCode}`;
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);
  const [retry, setRetry] = useState(0);
  const getPass = useCallback(() => { try { return sessionStorage.getItem(storageKey) || ''; } catch (_) { return ''; } }, [storageKey]);
  const check = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const run = generation.current;
    setChecking(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const headers = getPass() ? { 'X-Maintenance-Pass': getPass() } : {};
      const response = await fetch(statusUrl, { credentials: 'include', cache: 'no-store', headers, signal: controller.signal });
      if (!response.ok) throw new Error('Status unavailable');
      let value = await response.json();
      if (value.enabled && !value.canBypass && loginPath && statusUrl.includes('/maintenance-status')) {
        // Keep normal refresh available so an expired short-lived access cookie cannot strand a superuser.
        const refresh = await fetch(statusUrl.replace('/maintenance-status', '/auth/refresh'), { method: 'POST', credentials: 'include', signal: controller.signal });
        if (refresh.ok) {
          const refreshed = await fetch(statusUrl, { credentials: 'include', cache: 'no-store', headers, signal: controller.signal });
          if (refreshed.ok) value = await refreshed.json();
        }
      }
      if (typeof value.enabled !== 'boolean' || typeof value.canBypass !== 'boolean') throw new Error('Invalid status');
      if (run === generation.current) { setStatus(value); setError(false); }
    } catch (_) { if (run === generation.current) setError(true); } finally { clearTimeout(timeout); busy.current = false; if (run === generation.current) setChecking(false); }
  }, [statusUrl, getPass, loginPath]);
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const pass = hash.get('maintenance-pass');
    let active = true;
    const start = async () => {
      if (pass) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        try {
          if (passUrl) {
            const result = await fetch(passUrl, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: pass }) });
            if (!result.ok) throw new Error('Invalid pass');
          } else sessionStorage.setItem(storageKey, pass);
        } catch (_) { if (active) setError(true); }
      }
      if (active) { busy.current = false; check(); }
    };
    start();
    const timer = setInterval(check, 30000);
    const visible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', visible);
    // Attach passes only to this site's API, never arbitrary external requests.
    const target = new URL(statusUrl, window.location.origin);
    const prefix = target.pathname.includes('/maintenance-status') ? target.pathname.replace('/maintenance-status', '') : '/';
    const clients = [...new Set([axios, apiClient].filter(Boolean))];
    const interceptors = clients.map(client => ({ client, request: client.interceptors.request.use(config => {
      const url = new URL(config.url, new URL(config.baseURL || window.location.origin, window.location.origin));
      const token = getPass();
      if (token && url.origin === target.origin && (url.pathname === prefix || url.pathname.startsWith(prefix + '/') || prefix === '/')) {
        config.headers = { ...config.headers, 'X-Maintenance-Pass': token };
      }
      return config;
    }), response: client.interceptors.response.use(response => response, error => {
      if (['SITE_MAINTENANCE', 'MAINTENANCE_UNAVAILABLE'].includes(error.response?.data?.code)) check();
      return Promise.reject(error);
    }) }));
    return () => { generation.current += 1; active = false; clearInterval(timer); document.removeEventListener('visibilitychange', visible); interceptors.forEach(({ client, request, response }) => { client.interceptors.request.eject(request); client.interceptors.response.eject(response); }); };
  }, [check, getPass, passUrl, retry, statusUrl, storageKey, apiClient]);
  const authPage = loginPath && (pathname === loginPath || pathname.startsWith('/auth/') || pathname.startsWith('/loginhandshake') || pathname === '/handshake');
  if (authPage || (status && !error && (!status.enabled || status.canBypass))) return children;
  return (
    <main style={{ minHeight: '100dvh', width: '100%', boxSizing: 'border-box', padding: 24, display: 'grid', placeItems: 'center', background: '#f3f6f8', color: '#102c38', fontFamily: 'system-ui, sans-serif' }}>
      <section style={{ width: 'min(100%, 560px)', boxSizing: 'border-box', padding: '48px 32px', border: '1px solid #e0e8eb', borderRadius: 24, background: '#fff', boxShadow: '0 20px 70px #102c380a' }}>
        <div style={{ color: '#087f70', fontSize: 11, letterSpacing: '0.18em', fontWeight: 800 }}>SPORTS SOUTH</div>
        <div aria-hidden='true' style={{ marginTop: 32, width: 54, height: 54, borderRadius: 16, display: 'grid', placeItems: 'center', background: '#eaf5f2', color: '#087f70', fontSize: 26 }}>↻</div>
        <h1 style={{ fontSize: 34, letterSpacing: '-0.045em', margin: '24px 0 16px', lineHeight: 1.15 }}>{error ? 'Temporarily unavailable' : status ? 'We’ll be back shortly.' : 'Connecting…'}</h1>
        <p role='status' style={{ color: '#526570', fontSize: 16, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{error ? 'We can’t check website availability right now. Please try again shortly.' : status?.message || 'Checking website availability.'}</p>
        <button disabled={checking} onClick={() => setRetry(n => n + 1)} style={{ marginTop: 16, padding: '12px 20px', border: 0, borderRadius: 10, background: '#087f70', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>{checking ? 'Checking…' : 'Check again'}</button>
        <p style={{ fontSize: 12, color: '#73818a', lineHeight: 1.6, marginTop: 24 }}>This page checks automatically and will reopen when maintenance is complete.</p>
        {loginPath && <a href={`${loginPath}?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} style={{ color: '#087f70', fontSize: 12 }}>Superuser sign in</a>}
      </section>
    </main>
  );
}
